import clsx from 'clsx';
import { addDays, formatISO, parseISO } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import CreateEditInput from '@/modules/settings/company/components/Inputs/Modals/CreateEditInput';
import {
  useGetProductsList,
  useGetWarehouseCategoriesList,
} from '@/modules/warehouseManagement/shared/apiHooks';
import { IWarehouseManagement } from '@/modules/warehouseManagement/shared/types';
import CollapsedInput from '@/modules/warehouseManagement/warehouses/components/InboundDelivery/CreateEditInboundDelivery/Steps/Inputs/Collapsed';
import { InputField, SelectField, Typography } from '@/shared/components/atoms';
import CalendarDatePicker from '@/shared/components/atoms/CalendarDatePicker';
import EditableExpandableField from '@/shared/components/atoms/EditableExpandableField';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { useDidUpdateEffect } from '@/shared/hooks/useDidUpdateEffect';
import { getMeasurementUnits, getUnitName } from '@/shared/lib/convert-units';
import { IGeneral } from '@/shared/types';

const Input: React.FC<{
  index: number;
  handleAppend: IGeneral.IVoidFunction;
  handleRemove: IGeneral.IVoidFunction;
}> = ({ index, handleAppend, handleRemove }) => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [selectedAsset] = useStore((store) => store?.asset);

  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<IWarehouseManagement.ICreateInboundDeliveryFormValues>();

  const warehouseId = watch('warehouse.id');
  const productsWatcher = watch('products');
  const product = productsWatcher?.[index]?.product;
  const isSeeds = product?.measurementType === 'SEEDS';
  const batchStatus = productsWatcher?.[index]?.status;
  const categoryId = watch(`products.${index}.categoryId`);
  const packageUnit = watch(`products.${index}.packageUnit`);
  const productionDate = watch(`products.${index}.productionDate`);
  const editId = watch('editId');

  const { data: warehouseCategories, isLoading: isLoadingCategories } =
    useGetWarehouseCategoriesList({
      entityId: selectedAsset?.id,
      warehouseId: warehouseId,
    });

  const { data: products, isLoading: isLoadingProducts } = useGetProductsList({
    countryCode: selectedAsset?.country,
    entityId: selectedAsset?.id,
    warehouseId: warehouseId,
    categoryId: categoryId ?? undefined,
  });

  useDidUpdateEffect(() => {
    setValue(`products.${index}.product`, null);
  }, [categoryId]);

  useDidUpdateEffect(() => {
    setValue(`products.${index}.packageUnit`, null);
  }, [product?.id]);

  return (
    <EditableExpandableField
      fieldArrayName='products'
      addFieldText={t('add-input')}
      editFieldText={t('edit-input')}
      addAnotherFieldText={t('add-another-input')}
      index={index}
      length={productsWatcher?.length}
      collapsedHeader={
        <div className='flex items-center gap-8'>
          <Typography variant='subtitle' weight='medium'>
            {product?.name}
          </Typography>
        </div>
      }
      collapsedContent={<CollapsedInput input={productsWatcher?.[index]} />}
      canAppend={!editId}
      canDelete={batchStatus === 'unused'}
      actionsDisabled={batchStatus === 'used'}
      actionsDisabledTooltipMessage={t('input-edit-locked')}
      handleAppend={handleAppend}
      handleRemove={handleRemove}
    >
      <div className='grid grid-cols-2 gap-16'>
        <SelectField<IGeneral.NameIdPair, false>
          id={`products.${index}.categoryId`}
          isDisabled={!!watch('editId')}
          width='col-span-2'
          borderColor={themeColors().outline.variant}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          isSearchable
          list={warehouseCategories || []}
          getLabel={(item: IGeneral.NameIdPair) => item?.name}
          getValue={(item: IGeneral.NameIdPair) => item?.id}
          required
          isLoading={isLoadingCategories}
          placeholder={t('item-type')}
          label={t('item-type')}
        />
        <SelectField<IWarehouseManagement.IProductDetailsItem, false>
          id={`products.${index}.product`}
          errorMessageId={`products.${index}.product.id`}
          width='col-span-2'
          borderColor={themeColors().outline.variant}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          isSearchable
          list={products?.items || []}
          getLabel={(item: IWarehouseManagement.IProductDetailsItem) =>
            item?.name
          }
          getValue={(item: IWarehouseManagement.IProductDetailsItem) => ({
            name: item.name,
            id: item.id,
            measurementType: item.measurementType,
            activeSubstances: item?.activeSubstances ?? null,
          })}
          value={products?.items.filter((item) => item.id === product?.id)}
          required
          isLoading={isLoadingProducts}
          placeholder={t('select-product-name')}
          label={t('input')}
          isDisabled={!categoryId || batchStatus === 'partiallyUsed'}
          customOption={(option) => (
            <li className='list-none' key={option?.data?.id}>
              <Typography
                variant='subtitle1'
                weight='bold'
                className='capitalize'
              >
                {option?.label}
              </Typography>
              <div className='mt-8 flex flex-wrap gap-1'>
                {option?.data?.activeSubstances?.map((item, i) => (
                  <Typography
                    key={item.id}
                    variant='caption'
                    weight='medium'
                    className='capitalize'
                  >
                    {item.name} {item.concentration}%{' '}
                    <span
                      className={clsx(
                        i === option.data.activeSubstances.length - 1 &&
                          'hidden'
                      )}
                    >
                      -
                    </span>
                  </Typography>
                ))}
              </div>
            </li>
          )}
          createOption={{
            title: t('create-new-custom-input'),
            onClick: () =>
              openModal(<CreateEditInput full={false} />, {
                title: t('create-new-custom-input'),
              }),
          }}
        />
        <InputField
          id={`products.${index}.countOfPackages`}
          py='!py-14'
          borderColor='border-outline-variant'
          {...register(`products.${index}.countOfPackages`, {
            setValueAs: (value) => (value ? Number(value) : undefined),
          })}
          autoComplete='off'
          label={t('no-of-packages')}
          placeholder={t('no-of-packages')}
          error={!!errors?.products?.[index]?.countOfPackages}
          errorMessage={errors?.products?.[index]?.countOfPackages?.message}
          type='number'
          step={0.01}
          required
        />
        <InputField
          id={`products.${index}.packagePrice`}
          py='!py-14'
          borderColor='border-outline-variant'
          {...register(`products.${index}.packagePrice`, {
            setValueAs: (value) => (value ? Number(value) : undefined),
          })}
          autoComplete='off'
          label={t('package-price')}
          placeholder={t('package-price')}
          error={!!errors?.products?.[index]?.packagePrice}
          errorMessage={errors?.products?.[index]?.packagePrice?.message}
          type='number'
          step={0.01}
          required
          disabled={batchStatus === 'partiallyUsed'}
        />
        <SelectField
          id={`products.${index}.packageUnit`}
          borderColor={themeColors().outline.variant}
          py='0.5rem'
          px='0.5rem'
          borderRadius='1rem'
          isSearchable
          isDisabled={!product?.id || batchStatus === 'partiallyUsed'}
          label={t('measurement-unit')}
          getLabel={(item: { label: string }) => item.label}
          getValue={(item: { value: string }) => item.value}
          required
          list={getMeasurementUnits(
            product?.measurementType.toLowerCase() as
              | 'weight'
              | 'volume'
              | 'seeds',
            selectedAsset?.measurementSystem.toLowerCase()
          )?.map((item) => ({
            label: t(getUnitName(item)),
            value: item,
          }))}
          placeholder={t('measurement-unit')}
        />
        <InputField
          id={`products.${index}.packageSize`}
          {...register(`products.${index}.packageSize`, {
            setValueAs: (value) => (value ? Number(value) : undefined),
          })}
          py='!py-14'
          borderColor='border-outline-variant'
          disabled={!packageUnit || batchStatus === 'partiallyUsed'}
          autoComplete='off'
          label={t('weight-qty')}
          placeholder={t('weight-qty')}
          error={!!errors?.products?.[index]?.packageSize}
          errorMessage={errors?.products?.[index]?.packageSize?.message}
          type='number'
          step={0.00001}
          required
          suffix={
            <Typography className='w-56 truncate text-end'>
              {t(packageUnit ?? '')}
            </Typography>
          }
        />
        <Controller
          name={`products.${index}.productionDate`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CalendarDatePicker
              label={t('production-date')}
              placeholder={t('production-date')}
              error={errors?.products?.[index]?.productionDate?.message}
              disabled={batchStatus === 'partiallyUsed'}
              required={!isSeeds}
              maxDate={new Date()}
              value={value ? parseISO(value) : undefined}
              onChange={(date) => {
                if (date)
                  onChange(formatISO(date as Date, { representation: 'date' }));
              }}
            />
          )}
        />
        <Controller
          name={`products.${index}.expiryDate`}
          control={control}
          render={({ field: { onChange, value } }) => (
            <CalendarDatePicker
              label={t('expiry-date')}
              placeholder={t('expiry-date')}
              error={errors?.products?.[index]?.expiryDate?.message}
              disabled={!productionDate || batchStatus === 'partiallyUsed'}
              required={!isSeeds || !!productionDate}
              minDate={addDays(
                productionDate ? parseISO(productionDate) : new Date(),
                1
              )}
              value={value ? parseISO(value) : undefined}
              onChange={(date) => {
                if (date)
                  onChange(formatISO(date as Date, { representation: 'date' }));
              }}
            />
          )}
        />
      </div>
    </EditableExpandableField>
  );
};

export default Input;
